import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { normalizeAvatarUrl } from '@/utils/avatar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * MY PROFILE, FROM ANY PAGE — DP106 Part 1b, collapsed in DP109 ruling 6.
 *
 * The account menu is shell furniture, so its dialog is too. It was held in
 * UserDashboard alone: My Desk opened it, the Project Desk passed `() => {}`,
 * and every other page fell through to `/settings/account` — the Change
 * Password screen — so My Profile and Change Password did the same thing.
 *
 * DP109 ruling 6: this is now the ONLY profile dialog. My Desk's ~1,000-line
 * two-tab version is no longer reachable from the account menu; what it held and
 * this did not — email, bio, and the notification preferences — is here.
 *
 * EVERY FETCH HAS A .catch, AND THAT IS THE POINT. My Desk's opened on a
 * `Promise.all` of three calls with a `.catch` on one, so a `reminder-settings`
 * failure rejected the lot and THE DIALOG DID NOT OPEN — a person could not
 * change their photograph because a digest setting would not load. Here the
 * profile is the only thing that can stop it opening; preferences are fetched
 * beside it and a failure costs that section, nothing else.
 */
const NOTIFY_ROWS = [
  ['client_selection_approval', 'A client approves a selection'],
  ['client_selection_rejection', 'A client rejects a selection'],
  ['new_memo_added', 'A memo is added'],
  ['site_visit_logged', 'A site visit is logged'],
  ['new_meeting_note', 'A meeting note is written'],
  ['document_signed', 'A document is signed'],
  ['phase_changed', 'A project changes phase'],
  ['email_enabled', 'Send these by email'],
  ['browser_push_enabled', 'Show these in the browser'],
];

export const ProfileDialog = ({ darkMode = false }) => {
  const [open, setOpen] = React.useState(false);
  const [profile, setProfile] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [imgFailed, setImgFailed] = React.useState(false);
  const [prefs, setPrefs] = React.useState(null);       // null = not loaded
  const [prefsError, setPrefsError] = React.useState(null);
  const [reminders, setReminders] = React.useState(null);
  const [remindersError, setRemindersError] = React.useState(null);
  const fileRef = React.useRef(null);
  const userId = typeof window !== 'undefined' ? localStorage.getItem('userId') : null;

  const load = React.useCallback(async () => {
    const res = await axios.get(`${API}/users/${userId}/profile`);
    setProfile(res.data);
    setImgFailed(false);
    return res.data;
  }, [userId]);

  /* Fetched SEPARATELY and caught SEPARATELY. Neither of these can stop the
     dialog opening, and neither can stop the other from loading. */
  const loadPrefs = React.useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users/${userId}/notification-preferences`);
      setPrefs(res.data);
      setPrefsError(null);
    } catch {
      setPrefs(null);
      setPrefsError('Your notification settings could not be loaded.');
    }
    try {
      const res = await axios.get(`${API}/users/${userId}/reminder-settings`);
      setReminders(res.data);
      setRemindersError(null);
    } catch {
      setReminders(null);
      setRemindersError('Your reminder settings could not be loaded.');
    }
  }, [userId]);

  /* The rail holds its own module-level copy of the profile, so a dialog that
     changes the picture has to say so or the mark keeps the old one until a
     reload — FixedBottomNav's contract, at the top of that file. */
  const announce = (p) => window.dispatchEvent(new CustomEvent('keymade:user-updated', {
    detail: {
      name: [p?.first_name, p?.last_name].filter(Boolean).join(' ') || p?.email,
      avatar: p?.avatar ?? null,
    },
  }));

  React.useEffect(() => {
    const onOpen = async (e) => {
      if (!userId) return;   // no session: the menu reports it instead
      e.preventDefault();    // tells the menu this dialog took the click
      try {
        await load();
        setOpen(true);
        loadPrefs();         // deliberately NOT awaited, deliberately NOT fatal
      } catch {
        toast.error('Your profile could not be loaded.');
      }
    };
    window.addEventListener('km:open-profile', onOpen);
    return () => window.removeEventListener('km:open-profile', onOpen);
  }, [userId, load, loadPrefs]);

  const set = (k) => (ev) => setProfile((p) => ({ ...p, [k]: ev.target.value }));

  const uploadPhoto = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    const body = new FormData();
    body.append('file', file);
    try {
      await axios.post(`${API}/users/${userId}/avatar`, body,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      announce(await load());
      toast.success('Photo saved.');
    } catch {
      toast.error('The photo could not be saved.');
    }
  };

  const removePhoto = async () => {
    try {
      await axios.put(`${API}/users/${userId}/profile`, { avatar: null });
      announce(await load());
      toast.success('Photo removed. Your initials will show instead.');
    } catch {
      toast.error('The photo could not be removed.');
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/users/${userId}/profile`, {
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        title: profile.title || '',
        phone: profile.phone || '',
        bio: profile.bio || '',
        // AD-2 — where this person's day starts, if not the company address.
        travel_own_start: !!profile.travel_own_start,
        travel_start_address: profile.travel_start_address || '',
      });
      announce(profile);
      toast.success('Profile saved.');
      setOpen(false);
    } catch {
      toast.error('Your profile could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const setNotify = async (key, value) => {
    const before = prefs;
    setPrefs((p) => ({ ...p, user_overrides: { ...p.user_overrides, [key]: value },
                       effective: { ...p.effective, [key]: value } }));
    try {
      await axios.put(`${API}/users/${userId}/notification-preferences`, { [key]: value });
    } catch {
      setPrefs(before);
      toast.error('That setting could not be saved.');
    }
  };

  const setReminder = async (patch) => {
    const before = reminders;
    const next = { ...reminders, ...patch };
    setReminders(next);
    try {
      await axios.put(`${API}/users/${userId}/reminder-settings`, {
        daily_digest_enabled: next.daily_digest_enabled,
        digest_time: next.digest_time,
        days_before_reminder: Number(next.days_before_reminder) || 3,
        remind_overdue: next.remind_overdue,
        remind_ball_in_court: next.remind_ball_in_court,
        muted_projects: next.muted_projects || [],
      });
    } catch {
      setReminders(before);
      toast.error('That reminder setting could not be saved.');
    }
  };

  if (!profile) return null;
  const url = imgFailed ? null : normalizeAvatarUrl(profile.avatar);
  const initials = ([profile.first_name, profile.last_name].filter(Boolean)
    .map((n) => n[0]).join('') || (profile.email || 'U').slice(0, 2)).toUpperCase();
  const label = darkMode ? 'text-gray-300' : '';

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {/* the dark surface comes from the token layer, not a hex: the same
          data-km-surface the rail and the sheets use */}
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto"
                     data-km-surface={darkMode ? 'dark' : undefined}
                     style={darkMode ? { background: 'var(--km-paper)', borderColor: 'var(--km-rule)' } : undefined}
                     data-testid="shell-profile-dialog">
        <DialogHeader>
          <DialogTitle className={darkMode ? 'text-white' : ''}>My Profile</DialogTitle>
          <DialogDescription className={darkMode ? 'text-gray-400' : ''}>
            Your photo, your details and what you are told about. The same dialog on every page.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="profile">
          <TabsList data-testid="shell-profile-tabs">
            <TabsTrigger value="profile" data-testid="shell-profile-tab-profile">Profile</TabsTrigger>
            <TabsTrigger value="notifications" data-testid="shell-profile-tab-notifications">Notifications</TabsTrigger>
          </TabsList>

          <TabsContent value="profile">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-md overflow-hidden flex items-center justify-center border"
                   style={{ borderColor: 'var(--km-rule)', background: 'var(--km-paper-2)' }}
                   data-testid="shell-profile-avatar">
                {url
                  ? <img src={url} alt="" className="w-full h-full object-cover"
                         onError={() => setImgFailed(true)}
                         data-testid="shell-profile-avatar-img" />
                  : <span className="text-sm font-bold" data-testid="shell-profile-avatar-initials">{initials}</span>}
              </div>
              <div className="flex flex-col gap-2">
                <input ref={fileRef} type="file" accept="image/*" className="hidden"
                       onChange={uploadPhoto} data-testid="shell-profile-photo-input" />
                <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}
                        data-testid="shell-profile-photo-btn">
                  {url ? 'Change photo' : 'Add a photo'}
                </Button>
                {profile.avatar && (
                  <Button type="button" variant="ghost" size="sm" onClick={removePhoto}
                          data-testid="shell-profile-photo-remove">Remove photo</Button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4">
              <div>
                <Label className={label}>First name</Label>
                <Input value={profile.first_name || ''} onChange={set('first_name')}
                       data-testid="shell-profile-first-name" />
              </div>
              <div>
                <Label className={label}>Last name</Label>
                <Input value={profile.last_name || ''} onChange={set('last_name')}
                       data-testid="shell-profile-last-name" />
              </div>
              <div>
                <Label className={label}>Title</Label>
                <Input value={profile.title || ''} onChange={set('title')}
                       data-testid="shell-profile-title" />
              </div>
              <div>
                <Label className={label}>Phone</Label>
                <Input value={profile.phone || ''} onChange={set('phone')}
                       data-testid="shell-profile-phone" />
              </div>
              <div className="col-span-2">
                {/* DP109 ruling 6a — email, carried over from My Desk's dialog.
                    Read-only for the same reason it was there: it is the sign-in
                    identity and the profile endpoint does not change it. */}
                <Label className={label}>Email</Label>
                <Input value={profile.email || ''} readOnly disabled
                       data-testid="shell-profile-email" />
              </div>
              <div className="col-span-2">
                <Label className={label}>Bio</Label>
                <Textarea rows={3} value={profile.bio || ''} onChange={set('bio')}
                          data-testid="shell-profile-bio" />
              </div>
              {/* AD-2 — drive times start at the company address unless you say otherwise. */}
              <div className="col-span-2 space-y-2">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!profile.travel_own_start}
                         onChange={(e) => setProfile((p) => ({ ...p, travel_own_start: e.target.checked }))}
                         data-testid="travel-own-start" /> I start from a different address than the company
                </label>
                {profile.travel_own_start && (
                  <Input value={profile.travel_start_address || ''} onChange={set('travel_start_address')}
                         placeholder="Street, city, state" data-testid="travel-start-address" />
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setOpen(false)}
                      data-testid="shell-profile-cancel">Cancel</Button>
              <Button onClick={save} disabled={saving} data-testid="shell-profile-save">
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="notifications">
            {prefsError && (
              <p className="text-sm mb-3" style={{ color: 'var(--km-alert)' }}
                 data-testid="shell-profile-prefs-error">
                {prefsError} Everything else on this dialog still works.
              </p>
            )}
            {prefs && (
              <div className="space-y-3" data-testid="shell-profile-notify-list">
                {NOTIFY_ROWS.map(([key, text]) => {
                  const isOverridden = prefs.user_overrides?.[key] !== undefined
                                       && prefs.user_overrides?.[key] !== null;
                  return (
                    <div key={key} className="flex items-center justify-between gap-4">
                      <div>
                        <p className={`text-sm ${darkMode ? 'text-gray-200' : ''}`}>{text}</p>
                        {!isOverridden && (
                          <p className="text-xs" style={{ color: 'var(--km-ink-3)' }}>
                            following the firm’s default
                          </p>
                        )}
                      </div>
                      <Switch checked={!!prefs.effective?.[key]}
                              onCheckedChange={(v) => setNotify(key, v)}
                              data-testid={`shell-profile-notify-${key}`} />
                    </div>
                  );
                })}
              </div>
            )}

            <div className="mt-6 pt-4" style={{ borderTop: '1px solid var(--km-rule-soft)' }}>
              {remindersError && (
                <p className="text-sm" style={{ color: 'var(--km-alert)' }}
                   data-testid="shell-profile-reminders-error">{remindersError}</p>
              )}
              {reminders && (
                <div className="space-y-3" data-testid="shell-profile-reminders">
                  <div className="flex items-center justify-between gap-4">
                    <p className={`text-sm ${darkMode ? 'text-gray-200' : ''}`}>A daily digest</p>
                    <Switch checked={!!reminders.daily_digest_enabled}
                            onCheckedChange={(v) => setReminder({ daily_digest_enabled: v })}
                            data-testid="shell-profile-digest-enabled" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className={label}>Digest time</Label>
                      <Input type="time" value={reminders.digest_time || '08:00'}
                             onChange={(e) => setReminder({ digest_time: e.target.value })}
                             data-testid="shell-profile-digest-time" />
                    </div>
                    <div>
                      <Label className={label}>Warn me this many days ahead</Label>
                      <Input type="number" min="0" max="30"
                             value={reminders.days_before_reminder ?? 3}
                             onChange={(e) => setReminder({ days_before_reminder: e.target.value })}
                             data-testid="shell-profile-days-before" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <p className={`text-sm ${darkMode ? 'text-gray-200' : ''}`}>Remind me about overdue work</p>
                    <Switch checked={!!reminders.remind_overdue}
                            onCheckedChange={(v) => setReminder({ remind_overdue: v })}
                            data-testid="shell-profile-remind-overdue" />
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <p className={`text-sm ${darkMode ? 'text-gray-200' : ''}`}>Remind me when it is my move</p>
                    <Switch checked={!!reminders.remind_ball_in_court}
                            onCheckedChange={(v) => setReminder({ remind_ball_in_court: v })}
                            data-testid="shell-profile-remind-ball" />
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileDialog;
